import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ParkingService } from '../../../../api/gen/api/parking.service';
import { OperatorGroupService } from '../../../../api/gen/api/operatorGroup.service';
import { ParkingOperatorDto } from '../../../../api/gen/model/parkingOperatorDto';
import { OperatorGroupDto } from '../../../../api/gen/model/operatorGroupDto';
import { SortOrder } from '../../../../api/gen/model/sortOrder';

export interface AddOperatorGroupDialogData {
    group: OperatorGroupDto;
}

@Component({
    selector: 'app-add-operator-group',
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        MatProgressSpinnerModule,
        MatSnackBarModule
    ],
    templateUrl: './add-operator-group.component.html',
    styleUrl: './add-operator-group.component.scss'
})
export class AddOperatorGroupComponent implements OnInit {
    private dialogRef = inject(MatDialogRef<AddOperatorGroupComponent>);
    private data = inject<AddOperatorGroupDialogData>(MAT_DIALOG_DATA);
    private parkingService = inject(ParkingService);
    private operatorGroupService = inject(OperatorGroupService);
    private snackBar = inject(MatSnackBar);

    availableOperators = signal<ParkingOperatorDto[]>([]);
    selectedOperatorId = signal('');
    isLoading = signal(false);
    isAdding = signal(false);

    get group(): OperatorGroupDto {
        return this.data.group;
    }

    private get alreadyMemberIds(): Set<string> {
        return new Set(
            (this.group.operatorGroupMemberships ?? [])
                .map(m => m.operatorId!)
                .filter(Boolean)
        );
    }

    ngOnInit(): void {
        this.loadAvailableOperators();
    }

    private loadAvailableOperators(): void {
        this.isLoading.set(true);
        this.parkingService.parkingOperatorFilterPost(
            undefined, undefined, undefined, 0, 200, 'lastName', SortOrder.Ascending
        ).subscribe({
            next: (result) => {
                const members = this.alreadyMemberIds;
                this.availableOperators.set(
                    (result?.items ?? []).filter(o => o.id && !members.has(o.id))
                );
                this.isLoading.set(false);
            },
            error: () => {
                this.snackBar.open('Failed to load operators', 'Close', { duration: 3000 });
                this.isLoading.set(false);
            }
        });
    }

    getOperatorDisplayName(operator: ParkingOperatorDto): string {
        return [operator.firstName, operator.lastName].filter(Boolean).join(' ') || operator.email || 'Unknown';
    }

    onAdd(): void {
        const operatorId = this.selectedOperatorId();
        const groupId = this.group.id;
        if (!operatorId || !groupId) return;

        this.isAdding.set(true);
        this.operatorGroupService.operatorGroupsOperatorGroupIdOperatorsPost(groupId, { operatorId }).subscribe({
            next: () => {
                this.snackBar.open('Operator added to group successfully', 'Close', { duration: 3000 });
                this.dialogRef.close(true);
            },
            error: () => {
                this.snackBar.open('Failed to add operator to group', 'Close', { duration: 5000 });
                this.isAdding.set(false);
            }
        });
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }
}
